import get from 'lodash/get';
import { combineReducers } from 'redux';
import { createReducer } from 'redux-act';
import { createSelector, createSelectorCreator, lruMemoize } from 'reselect';
import shallowequal from 'shallowequal';
import { createResource } from '@gandi/resource/redux';
import { baseUrl as moduleBaseUrl, hashString, statePath as moduleStatePath } from '../utils';
export const VHOST_STATUS_BEING_CREATED = 'being_created';
export const VHOST_STATUS_BEING_DELETED = 'being_deleted';
export const VHOST_STATUS_DELETED = 'deleted';
export const VHOST_STATUS_LOCKED = 'locked';
export const VHOST_STATUS_OWNERSHIP_VALIDATED = 'ownership_validated';
export const VHOST_STATUS_PAUSED = 'paused';
export const VHOST_STATUS_RUNNING = 'running';
export const VHOST_STATUS_VALIDATION_FAILED = 'validation_failed';
export const VHOST_STATUS_WAITING_OWNERSHIP = 'waiting_ownership';
export const VHOST_ZONE_STATUS_UNKNOWN = 'unknown';
export const VHOST_ZONE_STATUS_LIVEDNS_CONFLICT = 'livedns_conflict';
export const VHOST_ZONE_STATUS_LIVEDNS_ERROR = 'livedns_error';
export const VHOST_ZONE_STATUS_LIVEDNS_DONE = 'livedns_done';
export const VHOST_ZONE_STATUS_ALTERED = 'altered';
export const VHOST_APPLICATION_STATUS_BEING_CREATED = 'being_created';
export const VHOST_APPLICATION_STATUS_CANCELED = 'canceled';
export const VHOST_APPLICATION_STATUS_ERROR = 'error';
export const VHOST_APPLICATION_STATUS_RUNNING = 'running';
export const VHOST_STRATEGY_REDIRECT = 'redirect_HTTP_to_HTTPS';
export const VHOST_STRATEGY_DISABLE = 'HTTP_only';
export const VHOST_STRATEGY_NONE = 'allow_HTTP_and_HTTPS';
export const statePath = `${moduleStatePath}.vhosts`;
export const baseUrl = `${moduleBaseUrl}/instances`;
export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 100;
export const DEFAULT_SORT_BY = '-date_created';
export const defaultParams = {
  page: DEFAULT_PAGE,
  per_page: DEFAULT_PER_PAGE,
  sort_by: DEFAULT_SORT_BY
};
export const computeLabel = params => hashString(JSON.stringify({
  ...defaultParams,
  ...params
}));
export const resourceName = 'resources-simplehosting.vhosts';
export const resource = createResource(resourceName);
export const fetch = (instanceId, params = {}, options = {}) => dispatch => {
  const mergedParams = {
    ...defaultParams,
    ...params
  };
  const label = computeLabel({
    ...mergedParams,
    id: instanceId
  });
  return dispatch(resource.fetch(`${baseUrl}/${instanceId}/vhosts`, {
    id: instanceId,
    label,
    params: {
      ...mergedParams
    },
    on200: response => response,
    not200: response => Promise.reject(response),
    ...options
  }));
};
export const create = (instanceId, payload, params, options = {}) => dispatch => {
  return dispatch(resource.createOne(`${baseUrl}/${instanceId}/vhosts`, payload, {
    label: instanceId,
    params,
    on2XX: response => response,
    not2XX: response => Promise.reject(response),
    ...options
  }));
};
export const fetchOne = (instanceId, fqdn, params, options = {}) => dispatch => {
  return dispatch(resource.fetchOne(`${baseUrl}/${instanceId}/vhosts/${fqdn}`, {
    label: `${instanceId}-${fqdn}`,
    params,
    on2XX: response => response,
    not2XX: response => Promise.reject(response),
    ...options
  }));
};
export const patch = (instanceId, fqdn, payload, params, options = {}) => dispatch => {
  return dispatch(resource.patchOne(`${baseUrl}/${instanceId}/vhosts/${fqdn}`, payload, {
    label: `${instanceId}-${fqdn}`,
    params,
    on2XX: response => response,
    not2XX: response => Promise.reject(response),
    ...options
  }));
};
export const remove = (instanceId, fqdn, params, options = {}) => dispatch => {
  return dispatch(resource.deleteOne(`${baseUrl}/${instanceId}/vhosts/${fqdn}`, undefined, {
    label: `${instanceId}-${fqdn}`,
    params,
    on2XX: response => response,
    not2XX: response => Promise.reject(response),
    ...options
  }));
};
export const invalidate = () => dispatch => dispatch(resource.invalidate());
export const initialState = {
  details: {},
  summaries: {}
};
export const handleFetchSuccess = (state, payload) => ({
  ...state,
  [payload.id]: {
    ...state[payload.id],
    data: payload.response.data.reduce((old, current) => ({
      ...old,
      [current.fqdn]: current
    }), state[payload.id] ? state[payload.id].data : {}),
    [payload.label]: {
      ids: payload.response.data.map(item => item.fqdn),
      totalCount: parseInt(payload.response.headers.get('x-total-count'), 10)
    }
  }
});
export const handleFetchOneSuccess = (state, payload) => ({
  ...state,
  [payload.label]: payload.response.data
});
export const summariesReducer = createReducer({
  [resource.fetch.success.getType()]: handleFetchSuccess
}, initialState.summaries);
export const detailsReducer = createReducer({
  [resource.fetchOne.success.getType()]: handleFetchOneSuccess
}, initialState.details);
export const reducer = combineReducers({
  details: detailsReducer,
  summaries: summariesReducer
});
const selectRawState = state => get(state, statePath, {});
export const selectRawSummaries = createSelector([selectRawState, (_, props) => props.id], (raw, instanceId) => raw.summaries[instanceId]);
export const selectSummariesData = createSelector([selectRawSummaries], raw => raw ? raw.data : undefined);
const createShallowEqualSelector = createSelectorCreator(lruMemoize, shallowequal);
export const selectComputedLabel = createShallowEqualSelector([selectRawSummaries, (_, props) => computeLabel(props)], (raw, label) => label);
export const selectSummariesMeta = createSelector([selectRawSummaries, selectComputedLabel], (raw, label) => raw ? raw[label] : undefined);
export const selectSummariesTotalCount = createSelector([selectSummariesMeta], meta => meta ? meta.totalCount : undefined);
export const selectSummaries = createSelector([selectSummariesData, selectSummariesMeta], (data, meta) => meta ? meta.ids.map(id => get(data, id)).filter(Boolean) : undefined);
export const selectRawDetails = createSelector([selectRawState], raw => raw.details);
export const selectDetail = createSelector([selectRawDetails, (_, props) => props], (details, props) => details[props]);
export const selectIsFetching = (state, props) => resource.selectIsFetching(state, selectComputedLabel(state, props));
export const selectIsLoaded = (state, props) => resource.selectIsLoaded(state, selectComputedLabel(state, props));
export const selectIsFailure = (state, props) => resource.selectIsFailure(state, selectComputedLabel(state, props));
export const selectIsForbidden = (state, props) => resource.selectIsFailure(state, {
  label: selectComputedLabel(state, props),
  status: 403
});
export const selectIsNotFound = (state, props) => resource.selectIsFailure(state, {
  label: selectComputedLabel(state, props),
  status: 404
});
export const selectIsToRefetch = (state, props) => resource.selectIsToRefetch(state, selectComputedLabel(state, props));
export const selectIsCreatingOne = (state, instanceId) => resource.selectIsCreatingOne(state, instanceId);
export const selectIsFetchingOne = (state, props) => resource.selectIsFetchingOne(state, props);
export const selectIsLoadedOne = (state, props) => resource.selectIsLoadedOne(state, props);
export const selectIsFailureOne = (state, props) => resource.selectIsFailureOne(state, props);
export const selectIsNotFoundOne = (state, props) => resource.selectIsFailureOne(state, {
  label: props,
  status: 404
});
export const selectIsToRefetchOne = (state, props) => resource.selectIsToRefetchOne(state, props);
export const selectIsPatchingOne = (state, props) => resource.selectIsPatchingOne(state, props);
export const selectIsDeletingOne = (state, props) => resource.selectIsDeletingOne(state, props);